import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ITaskV2 } from '../../task-v2/fi-task-v2';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { NgModel } from '@angular/forms';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { FiTaskV2Service } from '../../task-v2/fi-task-v2.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { SideListOptionType } from '@fi-shared/types';
import { FiTaskV2HelperService } from '../../task-v2/fi-task-v2-helper.service';

@Component({
    selector: 'fi-task-v2-edit',
    templateUrl: './fi-task-v2-edit.component.html',
    styleUrl: './fi-task-v2-edit.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-task-v2-edit' },
    standalone: false,
})
export class FiTaskV2EditComponent implements OnInit {
    @Input({ required: true }) task: ITaskV2;
    @Input({ required: true }) availableAccessRightFilterCollection: IAccessRightFilterCollection;
    @Input() isEditMode: boolean;
    @ViewChild(FiValidatingFormDirective) editForm: FiValidatingFormDirective;

    protected suppressDeadlineError = true;

    private initialTaskName: string;
    private initialDeadline: string;
    private deadlineChanged = false;

    constructor(
        private fiTaskV2Service: FiTaskV2Service,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiStateService: FiStateService,
        private fiTaskV2HelperService: FiTaskV2HelperService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.initialTaskName = this.task.Name;
        this.initialDeadline = this.task.Deadline;
        this.deadlineChanged = false;
        this.startWatchingChanges();
    }

    protected getHeaderText(): string {
        return this.fiTaskV2HelperService.getEditorHeaderText(this.isEditMode, this.initialTaskName);
    }

    protected onValidateError(inputsWithErrors: NgModel[]) {
        // Custom logic for deadline validation in edit mode
        if (this.isEditMode && !this.deadlineChanged) {
            const deadlineInput = inputsWithErrors.find((input) => input.name === 'datepickerModel');
            if (deadlineInput) {
                if (inputsWithErrors.length === 1) {
                    // Only deadline error, allow save
                    this.save();
                    return;
                }
                if (inputsWithErrors.length > 1) {
                    // Multiple errors, deadline is one of them, deadline not changed
                    // Suppress error message for datepicker field
                    this.suppressDeadlineError = true;
                    this.fiTaskV2HelperService.showAllItemsMandatoryErrorDialog();
                    return;
                }
            }
        }

        this.suppressDeadlineError = false;
        this.fiTaskV2HelperService.showAllItemsMandatoryErrorDialog();
    }

    protected onDeadlineDatePickerFocused() {
        //https://chainformation.atlassian.net/browse/FI-6499?focusedCommentId=77460&sourceType=mention
        // don't show error if user clicks on deadline picker without changing deadline date
        /*this.suppressDeadlineError = false;
        this.deadlineChanged = true;
        this.cdr.markForCheck();*/
    }

    protected updateFormValidity(): void {
        this.editForm.updateValidity();
    }

    protected onDeadlineChange(): void {
        if (this.task.Deadline !== this.initialDeadline) {
            this.deadlineChanged = true;
        }
        this.updateFormValidity();
    }

    protected save(): void {
        this.fiTaskV2Service.saveTask(this.task).then(() => {
            this.saveChangesWarningService.stop();
            this.fiTaskV2HelperService.showTaskSavedSnackbar();
            if (this.isEditMode) {
                this.fiStateService.go(FiStateNames.TaskV2.Details, { id: this.task.Id });
            } else {
                this.fiStateService.checkReturnToState(() => {
                    // editing task can be invoked from report widget, in that case we have to return to report
                    this.fiStateService.go(FiStateNames.TaskV2.Current, { filterType: this.getTaskListFilterType() });
                });
            }
        });
    }

    private startWatchingChanges(): void {
        this.saveChangesWarningService.start(
            () => this.task,
            this.fiTaskV2HelperService.getSaveChangesWarningData(this.isEditMode),
        );
    }

    private getTaskListFilterType(): SideListOptionType {
        return this.fiTaskV2HelperService.getAssignmentType(
            this.task.AccessRightFilterCollection,
            this.task.CreatedByUserId,
            this.task.AssignmentType,
        )
            ? SideListOptionType.TasksPrivate
            : SideListOptionType.TasksAssignedByMe;
    }
}
